import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { api } from '../api/endpoints'
import { Card, Empty, ErrorState, Loading } from '../components/StateViews'
import { formatDate, formatDateTime, formatNumber, roleLabel, snapshotStatusLabel } from '../format'
import type { SnapshotStatus } from '../types'

const STATUS_CLASS: Record<SnapshotStatus, string> = {
  applied: 'bg-emerald-50 text-emerald-800 ring-emerald-600/20',
  quarantined: 'bg-rose-50 text-rose-800 ring-rose-600/20',
  unchanged: 'bg-slate-100 text-slate-700 ring-slate-500/20',
  downloaded: 'bg-amber-50 text-amber-800 ring-amber-600/20',
}

// Administrators only (§8.2): snapshots, checks, quarantine, users.
export function System() {
  const queryClient = useQueryClient()
  const [selected, setSelected] = useState<string | null>(null)

  const snapshots = useQuery({
    queryKey: ['snapshots'],
    queryFn: ({ signal }) => api.snapshots(signal),
  })
  const users = useQuery({ queryKey: ['users'], queryFn: ({ signal }) => api.users(signal) })

  // skipToken instead of `selected!`: the query is disabled and the type stays
  // honest, with no assertion papering over a null.
  const detail = useQuery({
    queryKey: ['snapshots', 'detail', selected],
    queryFn: selected ? ({ signal }) => api.snapshot(selected, signal) : skipToken,
  })

  const ingest = useMutation({
    mutationFn: (force: boolean) => api.ingest(force),
    onSuccess: () => {
      // An ingestion moves everything downstream of it, not just the list.
      for (const key of [['snapshots'], ['health'], ['stats'], ['movements']]) {
        void queryClient.invalidateQueries({ queryKey: key })
      }
    },
  })

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-lg font-semibold">Sistema</h1>
          <p className="text-sm text-slate-500">
            Acquisizioni, controlli di sanità, quarantena, utenti.
          </p>
        </div>
        <button
          type="button"
          onClick={() => ingest.mutate(false)}
          disabled={ingest.isPending}
          className="rounded-md bg-slate-900 px-3 py-1.5 text-sm text-white hover:bg-slate-800 disabled:opacity-50"
        >
          {ingest.isPending ? 'acquisizione in corso…' : 'Acquisisci adesso'}
        </button>
      </div>

      {ingest.isSuccess && (
        <div role="status" className="rounded-md bg-slate-100 px-4 py-3 text-sm">
          {ingest.data.date}: <strong>{snapshotStatusLabel(ingest.data.status)}</strong> —{' '}
          {ingest.data.message}
        </div>
      )}
      {ingest.isError && <ErrorState error={ingest.error} />}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Snapshot">
          {snapshots.isPending ? (
            <Loading />
          ) : snapshots.isError ? (
            <ErrorState error={snapshots.error} onRetry={() => void snapshots.refetch()} />
          ) : snapshots.data.data.length === 0 ? (
            <Empty
              title="Nessuno snapshot."
              hint="Lancia la prima acquisizione con il pulsante qui sopra."
            />
          ) : (
            <div className="max-h-96 overflow-auto">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[42rem] text-sm">
                  <thead className="sticky top-0 border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th scope="col" className="px-4 py-2 font-medium">
                        Data
                      </th>
                      <th scope="col" className="px-4 py-2 font-medium">
                        Stato
                      </th>
                      <th scope="col" className="px-4 py-2 text-right font-medium">
                        MB
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {snapshots.data.data.map((snapshot) => (
                      <tr
                        key={snapshot.id}
                        aria-current={selected === snapshot.snapshot_date ? 'true' : undefined}
                        onClick={() => setSelected(snapshot.snapshot_date)}
                        className={`cursor-pointer hover:bg-slate-50 ${
                          selected === snapshot.snapshot_date ? 'bg-slate-50' : ''
                        }`}
                      >
                        <td className="px-4 py-2 tabular-nums">
                          {/* The date is the control: a row alone is not
                              reachable with the keyboard. */}
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation()
                              setSelected(snapshot.snapshot_date)
                            }}
                            className="underline decoration-dotted decoration-slate-300 hover:decoration-slate-600"
                          >
                            {formatDate(snapshot.snapshot_date)}
                          </button>
                        </td>
                        <td className="px-4 py-2">
                          <span
                            className={`rounded-full px-2 py-0.5 text-xs ring-1 ring-inset ${
                              STATUS_CLASS[snapshot.status] ?? ''
                            }`}
                          >
                            {snapshotStatusLabel(snapshot.status)}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">
                          {(snapshot.size_bytes / 1_048_576).toFixed(1)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Card>

        <Card title={selected ? `Controlli del ${formatDate(selected)}` : 'Controlli di sanità'}>
          {!selected ? (
            <Empty
              title="Scegli uno snapshot."
              hint="Il dettaglio mostra atteso e osservato per ogni controllo."
            />
          ) : detail.isPending ? (
            <Loading />
          ) : detail.isError ? (
            <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />
          ) : (
            <div className="max-h-96 overflow-y-auto p-4 text-sm">
              {detail.data.notes && (
                <p className="mb-3 rounded-md bg-rose-50 px-3 py-2 text-rose-800">
                  {detail.data.notes}
                </p>
              )}
              <ul className="space-y-1">
                {detail.data.checks.map((check) => (
                  <li key={check.name} className="flex items-start gap-2">
                    <span
                      aria-hidden="true"
                      className={
                        check.passed
                          ? 'text-emerald-600'
                          : check.blocking
                            ? 'text-rose-600'
                            : 'text-amber-600'
                      }
                    >
                      {check.passed ? '✓' : check.blocking ? '✕' : '!'}
                    </span>
                    <span className="flex-1">
                      <span className="font-medium">{check.name}</span>
                      <span className="text-slate-500">
                        {' '}
                        — {check.passed ? 'superato' : 'non superato'}: atteso {check.expected},
                        osservato {check.observed}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
              <h3 className="mt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Righe per file
              </h3>
              <ul className="mt-1 grid grid-cols-2 gap-x-4 text-xs text-slate-600">
                {Object.entries(detail.data.rows_per_file).map(([file, count]) => (
                  <li key={file} className="flex justify-between">
                    <span className="truncate">{file}</span>
                    <span className="tabular-nums">{formatNumber(count)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      </div>

      <Card title="Utenti">
        {users.isPending ? (
          <Loading />
        ) : users.isError ? (
          <ErrorState error={users.error} onRetry={() => void users.refetch()} />
        ) : users.data.length === 0 ? (
          <Empty title="Nessun utente." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[42rem] text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th scope="col" className="px-4 py-2 font-medium">
                    Email
                  </th>
                  <th scope="col" className="px-4 py-2 font-medium">
                    Ruolo
                  </th>
                  <th scope="col" className="px-4 py-2 font-medium">
                    2FA
                  </th>
                  <th scope="col" className="px-4 py-2 font-medium">
                    Ultimo accesso
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.data.map((user) => (
                  <tr key={user.id}>
                    <td className="px-4 py-2">{user.email}</td>
                    <td className="px-4 py-2">{roleLabel(user.role)}</td>
                    <td className="px-4 py-2">{user.totp_enabled ? 'sì' : 'no'}</td>
                    <td className="px-4 py-2 tabular-nums">{formatDateTime(user.last_login_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
